import { cn } from "@btnet/utilities-core";

import { BdsButtonGroup, BdsRollupButton } from "@buildertrend/components";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { useGridViewSelectCustomItemRender } from "commonComponents/entity/views/GridViewSelectCustomItem/GridViewSelectCustomItem";
import {
    GridViewColumn,
    type GridViewItem,
    type IGridSettingsFormValues,
} from "commonComponents/utilities/Grid/common/GridSettings/GridSettings.api.types";
import { UnsavedViewChangesHeader } from "commonComponents/utilities/Grid/common/UnsavedViewChangesHeader/UnsavedViewChangesHeader";
import { useShouldShowGridCardView } from "commonComponents/utilities/Grid/GridCardView/useShouldShowGridCardView";
import { type GridColumn } from "commonComponents/utilities/Grid/GridContainer.types";

import { type SavedViewsAndFiltersSettingsState } from "entity/filters/SavedViewsAndFiltersList/SavedViewsAndFiltersCard/SavedViewsAndFiltersCard.api.types";

import "./ManageSavedViews.less";

const getTriggerParent = (trigger: HTMLElement) => trigger.parentNode as HTMLElement;

export interface IManageSavedViewsProps {
    gridViews: BTSelectItem<GridViewItem>[];
    hasUnsavedChanges: boolean;
    columnsList?: GridColumn<unknown>[];
    selectedGridView: GridViewItem;
    onSaveView: (
        values: IGridSettingsFormValues,
        columns: GridViewColumn[],
        saveAsNew: boolean,
    ) => Promise<void>;
    onViewChanged: (field: string, value: number) => void;
    onAddNewSavedView: () => void;
    onResetView: () => void;
    setGridSettingsState: (state: SavedViewsAndFiltersSettingsState) => void;
    isSubOrOwner?: boolean;
    className?: string;
    /** When true, hides the component while the card-view trigger is active (narrow viewport / webview). */
    gridCardViewEnabled?: boolean;
    dropdownCanExpand?: boolean;
    /**
     * Overrides where the saved-views dropdown popup is rendered. Defaults to anchoring the popup
     * to its trigger's parent, which works for the usual sticky footer (fixed-positioned, with the
     * rest of the page providing scroll room for antd's auto-flip). Pass BTSelect's own default
     * (getClosestModal) or another container when the footer sits in a fixed-height viewport with
     * no room for the popup to flip into, e.g. Bills' AG Grid list (ado-282662).
     */
    getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
}

export const ManageSavedViews: React.FunctionComponent<IManageSavedViewsProps> = (props) => {
    const {
        gridViews,
        hasUnsavedChanges,
        columnsList,
        selectedGridView,
        onSaveView,
        onViewChanged,
        onAddNewSavedView,
        onResetView,
        setGridSettingsState,
        gridCardViewEnabled,
        dropdownCanExpand,
        getPopupContainer = getTriggerParent,
    } = props;

    const isCardView = useShouldShowGridCardView(gridCardViewEnabled ?? false);
    const customItemRender = useGridViewSelectCustomItemRender();

    if (isCardView) {
        return null;
    }

    const handleSettingsClicked = () => {
        setGridSettingsState("open");
    };

    const unsavedChanges = hasUnsavedChanges && (
        <UnsavedViewChangesHeader
            onSaveView={onSaveView}
            onAddNewSavedView={onAddNewSavedView}
            onResetView={onResetView}
            columnsList={columnsList}
            selectedGridView={selectedGridView}
        />
    );

    const handleSaveSettings = async () => {
        const formValues: IGridSettingsFormValues = {
            viewName: selectedGridView.viewName,
            isDefault: selectedGridView.isDefault,
            isPrivate: selectedGridView.isPrivate,
            savedView: selectedGridView.viewId,
            columns: columnsList!.map((col) => col.id),
        };
        await onSaveView(
            formValues,
            columnsList!.filter((c) => c.enabled).map((col) => new GridViewColumn(col)),
            false,
        );
    };

    const classes = cn("flex align-items-center", props.className);
    if (props.isSubOrOwner) {
        return (
            <div className={classes}>
                <BTButtonDeprecated
                    type="secondary"
                    data-testid="manageViews"
                    onClick={handleSettingsClicked}
                >
                    Manage Saved Views
                </BTButtonDeprecated>

                {unsavedChanges}
            </div>
        );
    }

    return (
        <div className={cn("ManageSavedViews", classes)}>
            <BTSelect
                id="savedView"
                data-testid="savedView"
                treeData={gridViews}
                value={selectedGridView.viewId}
                getPopupContainer={getPopupContainer}
                onChange={onViewChanged}
                onBlur={() => {}}
                customItemRender={customItemRender}
                showSearch={false}
                dropdownCanExpand={dropdownCanExpand}
                disableVirtualScroll={dropdownCanExpand}
            />
            <BdsButtonGroup
                id="manageViewsButtonGroup"
                testid="manageViewsButtonGroup"
                menuTestid="manageViewsButtonGroupMenu"
                rollupTestid="manageViewsButtonGroupRollup"
                displayType="tertiary"
                root="body"
            >
                {selectedGridView?.canEdit && hasUnsavedChanges && (
                    <BdsRollupButton
                        id="manageViewsSave"
                        testid="manageViewsSave"
                        text="Save changes"
                        onClick={handleSaveSettings}
                        value="manageViewsSave"
                        maxStage={0}
                    />
                )}
                {hasUnsavedChanges && (
                    <BdsRollupButton
                        id="manageViewsReset"
                        testid="manageViewsReset"
                        text="Reset"
                        onClick={onResetView}
                        value="manageViewsReset"
                        maxStage={0}
                    />
                )}
                {hasUnsavedChanges && (
                    <BdsRollupButton
                        id="manageViewsNewView"
                        testid="manageViewsNewView"
                        text="Save as new view"
                        onClick={onAddNewSavedView}
                        value="manageViewsNewView"
                        maxStage={0}
                    />
                )}
                <BdsRollupButton
                    id="manageViews"
                    testid="manageViews"
                    text="Manage saved views"
                    onClick={handleSettingsClicked}
                    value="manageViews"
                    maxStage={0}
                />
            </BdsButtonGroup>

            {hasUnsavedChanges && <div className="UnsavedChanges">Unsaved changes</div>}
        </div>
    );
};
